// src/components/AdminDashboard.jsx
// Admin dashboard — protected, only renders for is_admin === true users.
// Queries Supabase directly — no API route needed.
// Access is enforced by RLS: admin_read_all_* policies check is_admin on profiles.

import { useState, useEffect } from "react";
import { supabase } from "../lib/supabaseClient";

function StatCard({ label, value, sub }) {
  return (
    <div style={{
      background: "#111111",
      border: "1px solid #1c2333",
      borderRadius: 10,
      padding: "20px 24px",
      minWidth: 160,
    }}>
      <div style={{
        fontFamily: "var(--font-mono, JetBrains Mono, monospace)",
        fontSize: 9,
        color: "#8b949e",
        letterSpacing: ".14em",
        textTransform: "uppercase",
        marginBottom: 8,
      }}>
        {label}
      </div>
      <div style={{
        fontFamily: "var(--font-mono, JetBrains Mono, monospace)",
        fontSize: 32,
        fontWeight: 700,
        color: value === null || value === "—" ? "#8b949e" : "var(--accent, #3ddc84)",
        lineHeight: 1,
        marginBottom: sub ? 6 : 0,
      }}>
        {value === null ? "—" : value}
      </div>
      {sub && (
        <div style={{
          fontFamily: "var(--font-body, IBM Plex Sans, sans-serif)",
          fontSize: 11,
          color: "#8b949e",
        }}>
          {sub}
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard({ isAdmin }) {
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    if (!isAdmin || !supabase) { setLoading(false); return; }

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

        const [
          { count: totalSignups },
          { count: activeUsers7d },
          feedbackResult,
          betaResult,
          waitlistResult,
        ] = await Promise.all([
          supabase.from("profiles").select("id", { count: "exact", head: true }),
          supabase.from("profiles").select("id", { count: "exact", head: true }).gte("updated_at", sevenDaysAgo),
          supabase.from("feedback").select("id, message, current_page, created_at").order("created_at", { ascending: false }).limit(20),
          supabase.from("beta_codes").select("is_active"),
          supabase.from("waitlist").select("id", { count: "exact", head: true }),
        ]);

        const betaTotal     = betaResult.data?.length ?? 0;
        const betaUsed      = betaResult.data?.filter(r => !r.is_active).length ?? 0;
        const betaRemaining = betaResult.data?.filter(r =>  r.is_active).length ?? 0;

        setStats({
          totalSignups:  totalSignups ?? 0,
          activeUsers7d: activeUsers7d ?? 0,
          feedback: {
            rows: feedbackResult.data ?? [],
          },
          betaCodes: { total: betaTotal, used: betaUsed, remaining: betaRemaining },
          waitlistCount: waitlistResult.error ? null : (waitlistResult.count ?? 0),
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div style={{ padding: 60, textAlign: "center", fontFamily: "var(--font-mono)", color: "#8b949e", fontSize: 12 }}>
        ACCESS DENIED
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 960, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <div style={{
          fontFamily: "var(--font-mono, JetBrains Mono, monospace)",
          fontSize: 9,
          color: "#8b949e",
          letterSpacing: ".18em",
          textTransform: "uppercase",
          marginBottom: 6,
        }}>
          Admin
        </div>
        <div style={{
          fontFamily: "var(--font-display, Syne, sans-serif)",
          fontSize: 26,
          fontWeight: 700,
          color: "#c9d1d9",
        }}>
          Admin Dashboard
        </div>
      </div>

      {loading && (
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "#8b949e", letterSpacing: ".1em" }}>
          LOADING...
        </div>
      )}

      {error && (
        <div style={{
          background: "rgba(255,107,53,0.08)",
          border: "1px solid rgba(255,107,53,0.3)",
          borderRadius: 8,
          padding: "12px 16px",
          fontFamily: "var(--font-mono)",
          fontSize: 12,
          color: "#ff6b35",
          marginBottom: 24,
        }}>
          {error}
        </div>
      )}

      {stats && (
        <>
          {/* ── KPI row ───────────────────────────────────────────────── */}
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 32 }}>
            <StatCard label="Total Signups"  value={stats.totalSignups} />
            <StatCard label="Active Users"   value={stats.activeUsers7d} sub="last 7 days" />
            <StatCard label="Feedback"       value={stats.feedback.rows.length} sub="submissions (last 20)" />
            <StatCard
              label="Beta Codes"
              value={`${stats.betaCodes.used} / ${stats.betaCodes.total}`}
              sub={`${stats.betaCodes.remaining} remaining`}
            />
            <StatCard
              label="Waitlist"
              value={stats.waitlistCount}
              sub={stats.waitlistCount === null ? "table not built yet" : "signups"}
            />
          </div>

          {/* ── PostHog link ──────────────────────────────────────────── */}
          <div style={{
            background: "#111111",
            border: "1px solid #1c2333",
            borderRadius: 10,
            padding: "18px 24px",
            marginBottom: 24,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 24,
          }}>
            <div>
              <div style={{
                fontFamily: "var(--font-mono)",
                fontSize: 9,
                color: "#8b949e",
                letterSpacing: ".14em",
                textTransform: "uppercase",
                marginBottom: 4,
              }}>
                Most Visited Modules
              </div>
              <div style={{
                fontFamily: "var(--font-body)",
                fontSize: 13,
                color: "#c9d1d9",
              }}>
                Module analytics tracked via PostHog — check the{" "}
                <code style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--accent, #3ddc84)" }}>tab_viewed</code>{" "}
                event in your dashboard.
              </div>
            </div>
            <a
              href="https://eu.posthog.com"
              target="_blank"
              rel="noreferrer"
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 10,
                color: "var(--accent, #3ddc84)",
                textDecoration: "none",
                border: "1px solid rgba(61,220,132,0.3)",
                padding: "7px 14px",
                borderRadius: 6,
                letterSpacing: ".06em",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              OPEN POSTHOG →
            </a>
          </div>

          {/* ── Recent feedback ───────────────────────────────────────── */}
          <div style={{
            background: "#111111",
            border: "1px solid #1c2333",
            borderRadius: 10,
            overflow: "hidden",
          }}>
            <div style={{
              padding: "16px 24px",
              borderBottom: "1px solid #1c2333",
              fontFamily: "var(--font-mono)",
              fontSize: 9,
              color: "#8b949e",
              letterSpacing: ".14em",
              textTransform: "uppercase",
            }}>
              Recent Feedback
            </div>

            {stats.feedback.rows.length === 0 ? (
              <div style={{ padding: "24px", fontFamily: "var(--font-body)", fontSize: 13, color: "#8b949e" }}>
                No feedback yet.
              </div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #1c2333" }}>
                    {["Page", "Message", "Submitted"].map(h => (
                      <th key={h} style={{
                        padding: "8px 24px",
                        textAlign: "left",
                        fontFamily: "var(--font-mono)",
                        fontSize: 8,
                        color: "#8b949e",
                        letterSpacing: ".12em",
                        textTransform: "uppercase",
                        fontWeight: 400,
                      }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {stats.feedback.rows.map((row, i) => (
                    <tr
                      key={row.id}
                      style={{ borderBottom: i < stats.feedback.rows.length - 1 ? "1px solid #161b22" : "none" }}
                    >
                      <td style={{ padding: "12px 24px", fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--accent, #3ddc84)", whiteSpace: "nowrap" }}>
                        {row.current_page ?? "—"}
                      </td>
                      <td style={{ padding: "12px 24px", fontFamily: "var(--font-body)", fontSize: 12, color: "#c9d1d9", maxWidth: 480, wordBreak: "break-word" }}>
                        {row.message}
                      </td>
                      <td style={{ padding: "12px 24px", fontFamily: "var(--font-mono)", fontSize: 10, color: "#8b949e", whiteSpace: "nowrap" }}>
                        {new Date(row.created_at).toLocaleString("en-GB", {
                          day: "2-digit", month: "short", year: "numeric",
                          hour: "2-digit", minute: "2-digit",
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
